import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  FaTrophy, FaMapMarkerAlt, FaBox, FaBuilding,
  FaChevronDown
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { homeTranslations } from '../translations/home';
import { useManagedContent } from '../hooks/useManagedContent';
import { useManagedImages } from '../hooks/useManagedImages';
import { pageImageConfig } from '../content/pageImageConfig';
import '../App.css';

function Home() {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const location = useLocation();
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'home',
    language,
    defaultTranslations: homeTranslations
  });
  const images = useManagedImages({
    pageKey: 'home',
    defaultImages: pageImageConfig.home
  });

  useEffect(() => {
    // Handle hash navigation when coming from other pages
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const element = document.querySelector(targetId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <Header />

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-background">
          <img 
            src={images.hero}
            alt="Sports training facility"
            loading="lazy"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <motion.div 
            className="hero-text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-headline">{t.hero.headline}</h1>
            <h2 className="hero-subheading">{t.hero.subheading}</h2>
            <p className="hero-tagline">{t.hero.tagline}</p>
            <div className="hero-buttons">
              <Link to="/packages">
                <motion.button 
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.hero.ourPackages}
                </motion.button>
              </Link>
              <Link to="/locations">
                <motion.button 
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.hero.locations}
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
        <motion.div 
          className="scroll-indicator"
          style={{ opacity }}
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <FaChevronDown />
        </motion.div>
      </section>

      {/* Training Excellence Section */}
      <section id="training" className="training-excellence">
        <div className="container">
          <motion.div 
            className="training-content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-heading">{t.training.heading}</h2>
            <p className="section-text">{t.training.text}</p>
            <Link to="/packages">
              <motion.button 
                className="btn-learn-more"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t.training.learnMore}
              </motion.button>
            </Link>
          </motion.div>
          <motion.div 
            className="training-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="glass-card">
              <img 
                src={images.training}
                alt="Athlete training"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.about.heading}
          </motion.h2>
          <div className="about-content">
            <motion.div 
              className="about-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="about-description">{t.about.description}</p>
              <p className="about-expanded">{t.about.expanded}</p>
              <div className="about-buttons">
                <Link to="/about">
                  <motion.button 
                    className="btn-learn-more"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.about.learnMore}
                  </motion.button>
                </Link>
              </div>
            </motion.div>
            <motion.div 
              className="about-images"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={images.about}
                alt="Sports camp"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="locations-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.locations.heading}
          </motion.h2>
          <div className="locations-grid">
            <motion.div 
              className="location-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="location-image">
                <img 
                  src="https://i.postimg.cc/Pf6Vg1hB/Chat-GPT-Image-Jan-20-2026-01-45-19-PM.png" 
                  alt={t.locations.kranevo.sports}
                  loading="lazy"
                />
                <div className="location-overlay"></div>
              </div>
              <div className="location-content">
                <h3>{t.locations.kranevo.name}</h3>
                <p className="location-sports">{t.locations.kranevo.sports}</p>
                <p className="location-description">{t.locations.kranevo.desc}</p>
                <p className="location-address">
                  <FaMapMarkerAlt /> {t.locations.kranevo.address}
                </p>
                <Link to="/locations/kranevo">
                  <motion.button 
                    className="btn-view-details"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.locations.viewDetails}
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="location-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="location-image">
                <img 
                  src="https://i.postimg.cc/RF8pz52m/Chat-GPT-Image-Jan-19-2026-08-36-49-PM.png" 
                  alt={t.locations.sofiaMladost.sports}
                  loading="lazy"
                />
                <div className="location-overlay"></div>
              </div>
              <div className="location-content">
                <h3>{t.locations.sofiaMladost.name}</h3>
                <p className="location-sports">{t.locations.sofiaMladost.sports}</p>
                <p className="location-description">{t.locations.sofiaMladost.desc}</p>
                <p className="location-address">
                  <FaMapMarkerAlt /> {t.locations.sofiaMladost.address}
                </p>
                <Link to="/locations/sofia-mladost">
                  <motion.button 
                    className="btn-view-details"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.locations.viewDetails}
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="location-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="location-image">
                <img 
                  src="https://i.postimg.cc/d1XNKPgv/Chat-GPT-Image-Jan-19-2026-08-36-53-PM.png" 
                  alt={t.locations.sofiaNsa.sports}
                  loading="lazy"
                />
                <div className="location-overlay"></div>
              </div>
              <div className="location-content">
                <h3>{t.locations.sofiaNsa.name}</h3>
                <p className="location-sports">{t.locations.sofiaNsa.sports}</p>
                <p className="location-description">{t.locations.sofiaNsa.desc}</p>
                <p className="location-address">
                  <FaMapMarkerAlt /> {t.locations.sofiaNsa.address}
                </p>
                <Link to="/locations/sofia-nsa">
                  <motion.button 
                    className="btn-view-details"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.locations.viewDetails}
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="location-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="location-image">
                <img 
                  src="https://i.postimg.cc/0QrX32W4/Chat-GPT-Image-Jan-19-2026-08-36-57-PM.png" 
                  alt={t.locations.plovdiv.sports}
                  loading="lazy"
                />
                <div className="location-overlay"></div>
              </div>
              <div className="location-content">
                <h3>{t.locations.plovdiv.name}</h3>
                <p className="location-sports">{t.locations.plovdiv.sports}</p>
                <p className="location-description">{t.locations.plovdiv.desc}</p>
                <p className="location-address">
                  <FaMapMarkerAlt /> {t.locations.plovdiv.address}
                </p>
                <Link to="/locations/plovdiv">
                  <motion.button 
                    className="btn-view-details"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.locations.viewDetails}
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="location-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="location-image">
                <img 
                  src="https://i.postimg.cc/YCQ3CFcd/Chat-GPT-Image-Jan-20-2026-01-45-16-PM.png" 
                  alt={t.locations.sofiaBoyana.sports}
                  loading="lazy"
                />
                <div className="location-overlay"></div>
              </div>
              <div className="location-content">
                <h3>{t.locations.sofiaBoyana.name}</h3>
                <p className="location-sports">{t.locations.sofiaBoyana.sports}</p>
                <p className="location-description">{t.locations.sofiaBoyana.desc}</p>
                <p className="location-address">
                  <FaMapMarkerAlt /> {t.locations.sofiaBoyana.address}
                </p>
                <Link to="/locations/sofia-boyana">
                  <motion.button 
                    className="btn-view-details"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t.locations.viewDetails}
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="why-choose-us">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.whyChooseUs.heading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.whyChooseUs.subheading}
          </motion.p>
          <div className="features-grid">
            {[
              { icon: <FaTrophy />, ...t.whyChooseUs.features[0] },
              { icon: <FaMapMarkerAlt />, ...t.whyChooseUs.features[1] },
              { icon: <FaBox />, ...t.whyChooseUs.features[2] },
              { icon: <FaBuilding />, ...t.whyChooseUs.features[3] }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="packages-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.packages.heading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.packages.subheading}
          </motion.p>
          <motion.div 
            className="package-card featured"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -10 }}
          >
            <div className="popular-badge">{t.packages.popular}</div>
            <div className="package-content">
              <div className="package-image">
                <img 
                  src="https://i.postimg.cc/d0cqwgP5/Chat-GPT-Image-Jan-19-2026-08-44-58-PM.png" 
                  alt={t.packages.fullCampTitle}
                  loading="lazy"
                />
              </div>
              <div className="package-details">
                <h3>{t.packages.fullCampTitle}</h3>
                <p className="package-description">{t.packages.fullCampDesc}</p>
                <ul className="package-includes">
                  {t.packages.includes.map((item, i) => (
                    <li key={i}>✓ {item}</li>
                  ))}
                </ul>
                <div className="package-buttons">
                  <Link to="/packages">
                    <motion.button 
                      className="btn-primary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t.packages.viewDetails}
                    </motion.button>
                  </Link>
                  <Link to="/packages">
                    <motion.button 
                      className="btn-secondary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t.packages.discoverMore}
                    </motion.button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <motion.p 
            className="stats-tagline"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.stats.tagline}
          </motion.p>
          <div className="stats-grid">
            {[
              { number: "50+", label: t.stats.camps },
              { number: "10+", label: t.stats.sports },
              { number: "5", label: t.stats.locations },
              { number: "1000+", label: t.stats.athletes }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
          <Link to="/book">
            <motion.button 
              className="btn-primary large"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t.stats.bookNow}
            </motion.button>
          </Link>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.gallery.heading}
          </motion.h2>
          <div className="gallery-grid">
            {[
              "https://i.postimg.cc/0js3QhxC/Chat-GPT-Image-Jan-21-2026-05-22-55-PM.png",
              "https://i.postimg.cc/PJjcx0hY/Chat-GPT-Image-Jan-21-2026-05-32-31-PM.png",
              "https://i.postimg.cc/nrJ5zNZ4/Chat-GPT-Image-Jan-21-2026-05-32-37-PM.png",
              "https://i.postimg.cc/7L5jQgmK/Chat-GPT-Image-Jan-21-2026-05-55-51-PM.png",
              "https://i.postimg.cc/rsNHtgwV/Chat-GPT-Image-Jan-22-2026-01-36-25-PM.png",
              "https://i.postimg.cc/0js3Qhx3/Chat-GPT-Image-Jan-22-2026-01-36-29-PM.png"
            ].map((img, index) => (
              <motion.div 
                key={index}
                className="gallery-item"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, zIndex: 10 }}
              >
                <img src={img} alt={`Facility ${index + 1}`} loading="lazy" />
                <div className="gallery-overlay"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta">
        <div className="cta-background">
          <img 
            src={images.finalCta}
            alt="Elite training"
            loading="lazy"
          />
          <div className="cta-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{t.finalCta.heading}</h2>
            <p>{t.finalCta.text}</p>
            <Link to="/book">
              <motion.button 
                className="btn-primary large"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t.finalCta.button}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Home;
